"use strict";
/*******************************************************************************

    Contains the Lock / Unlock definitions

    These are the types that will ultimately
    replace the `signature` in the `Input` and the `address` in the `Output`.

    The Lock type contains a tag, allowing 4 different types of lock scripts:

    Key
        lock is a 64-byte public key,
        unlock is expected to be a signature.

    KeyHash
        lock is a hash of a 64-byte public key,
        unlock is expected to be a pair of <signature, public-key>.
        This form may be used for better privacy.

    Script
        lock is a script that will be evaluated
        by the engine. The unlock script may either
        be empty or only contain stack push opcodes.

    Redeem
        lock is a <redeem-hash>, and unlock may
        only contain stack push opcodes, where the
        last push will be read as the redeem script.

    Copyright:
        Copyright (c) 2020-2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
Object.defineProperty(exports, "__esModule", { value: true });
exports.Unlock = exports.Lock = exports.LockType = void 0;
var Hash_1 = require("../common/Hash");
var JSONValidator_1 = require("../utils/JSONValidator");
var Utils_1 = require("../utils/Utils");
var VarInt_1 = require("../utils/VarInt");
var Signature_1 = require("./Signature");
/**
 * The input lock types. 1 Byte unsigned integer
 */
var LockType;
(function (LockType) {
    /// lock is a 64-byte public key, unlock is the signature
    LockType[LockType["Key"] = 0] = "Key";
    /// lock is a 64-byte public key hash, unlock is a (sig, key) pair
    LockType[LockType["KeyHash"] = 1] = "KeyHash";
    /// lock is a script, unlock may be anything required by the lock script
    LockType[LockType["Script"] = 2] = "Script";
    /// lock is a 64-byte hash of a script, unlock is the script containing
    /// only stack pushes which will push the redeem script last
    LockType[LockType["Redeem"] = 3] = "Redeem";
})(LockType = exports.LockType || (exports.LockType = {}));
/**
 * Contains a tag and either a Hash or set of opcodes
 */
var Lock = /** @class */ (function () {
    /**
     * Constructor
     * @param type  Specifies the type of lock script
     * @param data May either be a Hash, or a sequence of opcodes
     */
    function Lock(type, data) {
        this.type = type;
        if (typeof data === "string")
            this.bytes = Buffer.from(data, "base64");
        else
            this.bytes = Buffer.from(data);
    }
    /**
     * The reviver parameter to give to `JSON.parse`
     * @param key   Name of the field being parsed
     * @param value The value associated with `key`
     * @returns A new instance of `Lock` if `key == ""`, `value` otherwise.
     */
    Lock.reviver = function (key, value) {
        if (key !== "")
            return value;
        JSONValidator_1.JSONValidator.isValidOtherwiseThrow("Lock", value);
        return new Lock(value.type, value.bytes);
    };
    /**
     * Collects data to create a hash.
     * @param buffer The buffer where collected data is stored
     */
    Lock.prototype.computeHash = function (buffer) {
        buffer.writeUInt8(this.type);
        Hash_1.hashPart(this.bytes, buffer);
    };
    /**
     * Generates a LockType.Key lock script.
     * @param key the public key which can unlock this lock script
     * @returns The lock script
     */
    Lock.fromPublicKey = function (key) {
        return new Lock(LockType.Key, key.data);
    };
    Object.defineProperty(Lock, "Null", {
        /**
         * The bytes consisting of null values for all bytes.
         * @returns The instance of Unlock
         */
        get: function () {
            return new Lock(LockType.Key, Buffer.alloc(0));
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Converts this object to its JSON representation
     */
    Lock.prototype.toJSON = function () {
        return {
            type: this.type,
            bytes: this.bytes.toString("base64"),
        };
    };
    /**
     * Returns the data size.
     */
    Lock.prototype.getNumberOfBytes = function () {
        return (Utils_1.Utils.SIZE_OF_BYTE + // Lock.type
            this.bytes.length); // Lock.bytes
    };
    /**
     * Serialize as binary data.
     * @param buffer - The buffer where serialized data is stored
     */
    Lock.prototype.serialize = function (buffer) {
        VarInt_1.VarInt.fromNumber(this.type, buffer, 1);
        VarInt_1.VarInt.fromNumber(this.bytes.length, buffer);
        buffer.writeBuffer(this.bytes);
    };
    /**
     * Deserialize as binary data.
     * @param buffer - The buffer to be deserialized
     */
    Lock.deserialize = function (buffer) {
        var type = VarInt_1.VarInt.toNumber(buffer, 1);
        var length = VarInt_1.VarInt.toNumber(buffer);
        return new Lock(type, Utils_1.Utils.readBuffer(buffer, length));
    };
    /**
     * Creates and returns a copy of this object.
     */
    Lock.prototype.clone = function () {
        return new Lock(this.type, this.bytes);
    };
    return Lock;
}());
exports.Lock = Lock;
/**
 * Contains a data tuple or a set of push opcodes
 */
var Unlock = /** @class */ (function () {
    /**
     * Constructor
     * @param data May be: <signature>, <signature, key>, <key, push opcodes>
     */
    function Unlock(data) {
        if (typeof data === "string")
            this.bytes = Buffer.from(data, "base64");
        else
            this.bytes = Buffer.from(data);
    }
    /**
     * The reviver parameter to give to `JSON.parse`
     * @param key   Name of the field being parsed
     * @param value The value associated with `key`
     * @returns A new instance of `Unlock` if `key == ""`, `value` otherwise.
     */
    Unlock.reviver = function (key, value) {
        if (key !== "")
            return value;
        JSONValidator_1.JSONValidator.isValidOtherwiseThrow("Unlock", value);
        return new Unlock(value.bytes);
    };
    /**
     * Collects data to create a hash.
     * @param buffer The buffer where collected data is stored
     */
    Unlock.prototype.computeHash = function (buffer) {
        buffer.writeBuffer(this.bytes);
    };
    /**
     * Generates a LockType.Key unlock script.
     * @param sig The signature that will be embedded in the script
     * @returns The unlock script
     */
    Unlock.fromSignature = function (sig) {
        return Unlock.fromSigPair(new Signature_1.SigPair(sig));
    };
    /**
     * Generates a LockType.Key unlock script.
     * @param sig The instance of SigPair that will be embedded in the script
     * @returns The unlock script
     */
    Unlock.fromSigPair = function (sig) {
        return new Unlock(sig.encode());
    };
    Object.defineProperty(Unlock, "Null", {
        /**
         * The bytes consisting of null values for all bytes.
         * @returns The instance of Unlock
         */
        get: function () {
            return new Unlock(Buffer.alloc(0));
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Converts this object to its JSON representation
     */
    Unlock.prototype.toJSON = function () {
        return {
            bytes: this.bytes.toString("base64"),
        };
    };
    /**
     * Returns the data size.
     */
    Unlock.prototype.getNumberOfBytes = function () {
        return this.bytes.length; // Unlock.bytes
    };
    /**
     * Serialize as binary data.
     * @param buffer - The buffer where serialized data is stored
     */
    Unlock.prototype.serialize = function (buffer) {
        VarInt_1.VarInt.fromNumber(this.bytes.length, buffer);
        buffer.writeBuffer(this.bytes);
    };
    /**
     * Deserialize as binary data.
     * @param buffer - The buffer to be deserialized
     */
    Unlock.deserialize = function (buffer) {
        var length = VarInt_1.VarInt.toNumber(buffer);
        return new Unlock(Utils_1.Utils.readBuffer(buffer, length));
    };
    /**
     * Creates and returns a copy of this object.
     */
    Unlock.prototype.clone = function () {
        return new Unlock(this.bytes);
    };
    return Unlock;
}());
exports.Unlock = Unlock;
//# sourceMappingURL=Lock.js.map